import React, { useState } from "react";
import {
  Button,
  Typography,
  Paper,
  Stack,
  ImageList,
  Fab,
  ImageListItemBar,
  alpha,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AuthContextForm, useAuthContext } from "components/AuthContext";
import Lightbox from "yet-another-react-lightbox";
import { default as VideoPlugin } from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";
import { Image, Video } from "pages/the-couple/Photos";
import { Close } from "@mui/icons-material";

export default function Upload() {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const { token, logout } = useAuthContext();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photos[index].src);
    setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
    setError(false);
    setSuccess(false);
    setSelectedPhoto(null);
  };

  const handleSetPhotos = (files) => {
    if (!files || files.length === 0) return;

    const newPhotos = [...files].map((file) => {
      const src = URL.createObjectURL(file);
      if (file.type.startsWith("image/")) {
        return {
          type: "image",
          src: src,
          alt: file.name,
          photo: file,
        };
      }

      return {
        type: "video",
        src: src,
        sources: [{ src: src, type: file.type }],
        alt: file.name,
        photo: file,
      };
    });

    setPhotos((prevPhotos) =>
      [...prevPhotos, ...newPhotos]
        .filter(
          (file, index, self) =>
            index === self.findIndex((f) => f.alt === file.alt)
        )
        .map((photo, index) => ({ ...photo, id: index }))
    );
    setError(false);
    setSuccess(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!photos) return;
    setUploading(true);
    await new Promise((r) => setTimeout(r, 5000));
    setSuccess(false);
    setError(false);
    const formData = new FormData();
    [...photos].forEach((photo) => {
      formData.append("photos", photo.photo);
    });
    const res = await fetch("/auth/photo_upload/upload", {
      method: "POST",
      headers: { authorization: token },
      body: formData,
    });
    setUploading(false);
    if (res.status === 401) {
      logout();
    } else if (res.ok) {
      setSuccess(true);
      setPhotos([]);
    } else {
      setError(true);
    }
  };

  return (
    <AuthContextForm>
      <Typography variant="h3" color="secondary.main" align="center">
        The Photos
      </Typography>
      <Paper sx={{ margin: "auto", padding: 4, maxWidth: 800 }}>
        <form onSubmit={handleUpload}>
          <Stack spacing={2}>
            <Typography variant="h5" color="secondary.main" align="center">
              Upload Your Pictures or Videos!
            </Typography>
            <Button
              variant="contained"
              component="label"
              color="secondary"
              disabled={uploading}
            >
              Choose Pictures or Videos
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => handleSetPhotos(e.target.files)}
                accept="image/*,video/*"
              />
            </Button>
            <Lightbox
              plugins={[VideoPlugin]}
              open={selectedPhoto !== null}
              close={() => setSelectedPhoto(null)}
              slides={photos}
              index={selectedPhoto}
            />
            {photos && photos.length > 0 && (
              <Box sx={{ maxHeight: "50vh", overflowY: "scroll" }}>
                <ImageList variant="masonry" cols={matches ? 1 : 2} gap={8}>
                  {photos.map((photo, idx) => (
                    <React.Fragment key={idx}>
                      {photo.type === "image" ? (
                        <Image
                          photo={photo}
                          onClick={() => setSelectedPhoto(idx)}
                        >
                          <ImageListItemBar
                            sx={{ background: "none" }}
                            position="top"
                            actionPosition="right"
                            actionIcon={
                              <Fab
                                size="small"
                                sx={(theme) => ({
                                  backgroundColor: alpha(
                                    theme.palette.secondary.main,
                                    0.6
                                  ),
                                  margin: theme.spacing(1),
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePhoto(idx);
                                }}
                              >
                                <Close />
                              </Fab>
                            }
                          />
                        </Image>
                      ) : (
                        <Video
                          video={photo}
                          key={idx}
                          onClick={() => setSelectedPhoto(idx)}
                        >
                          <ImageListItemBar
                            sx={{ background: "none" }}
                            position="top"
                            actionPosition="right"
                            actionIcon={
                              <Fab
                                size="small"
                                sx={(theme) => ({
                                  backgroundColor: alpha(
                                    theme.palette.secondary.main,
                                    0.6
                                  ),
                                  margin: theme.spacing(1),
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePhoto(idx);
                                }}
                              >
                                <Close />
                              </Fab>
                            }
                          />
                        </Video>
                      )}
                    </React.Fragment>
                  ))}
                </ImageList>
              </Box>
            )}
            {photos && photos.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  photos.forEach((photo) => URL.revokeObjectURL(photo.src));
                  setPhotos([]);
                }}
                disabled={uploading || photos.length === 0}
              >
                Clear
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={uploading || photos.length === 0}
            >
              {uploading ? <CircularProgress size={24} /> : "Upload"}
            </Button>
            {success && (
              <Typography alignSelf="center" color="success.main" variant="h5">
                Upload successful!
              </Typography>
            )}
            {error && (
              <Typography alignSelf="center" color="error.main" variant="h5">
                Upload failed! Please try again.
              </Typography>
            )}
          </Stack>
        </form>
      </Paper>
    </AuthContextForm>
  );
}
